"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";

import { isAnonAnalyticsOff } from "./anonymous-analytics";

const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.posthog.com";

const POSTHOG_PLACEHOLDER = "phc_your_project_key_here";

function isPostHogConfigured(): boolean {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  return Boolean(key && key.trim() !== "" && key !== POSTHOG_PLACEHOLDER);
}

function PostHogPageView(): null {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!isPostHogConfigured() || !pathname) return;

    // Full absolute URL (PostHog expects $current_url to include the origin;
    // a bare pathname breaks URL/host breakdowns in insights).
    const url = searchParams?.toString()
      ? `${window.origin}${pathname}?${searchParams.toString()}`
      : `${window.origin}${pathname}`;

    posthog.capture("$pageview", { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}

/**
 * Bridges Termly's consent state to PostHog's opt-in/out.
 *
 * PostHog is bundled (not a <script> tag), so Termly's resource-blocker can't
 * auto-gate it — we do it here. Termly's Event API exposes consent via
 * `Termly.getConsentState()` (categories: essential / performance / analytics /
 * advertising / social_networking) and notifies changes through
 * `Termly.on('initialized' | 'consent', cb)`. We opt PostHog IN only when the
 * analytics (or performance) category is granted, and opt back OUT otherwise.
 *
 * Docs: https://support.termly.io/hc/en-us/articles/30710543895697-Consent-API-documentation
 */
type TermlyConsent = {
  analytics?: boolean;
  performance?: boolean;
} & Record<string, boolean>;

type TermlyApi = {
  getConsentState?: () => TermlyConsent | undefined;
  on?: (
    event: "initialized" | "consent",
    cb: (data?: TermlyConsent) => void,
  ) => void;
};

function getTermly(): TermlyApi | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { Termly?: TermlyApi }).Termly;
}

// Without cookie consent a visitor may still be counted cookielessly (see
// anonymous-analytics.ts). That count is pageviews and our own CTA events only:
// no session replay, no autocapture or heatmaps. Those come back on consent.
let trimmedForCookieless = false;

function trimToAnonymousCounts() {
  if (trimmedForCookieless) return;
  trimmedForCookieless = true;
  posthog.set_config({ autocapture: false, capture_heatmaps: false });
  posthog.stopSessionRecording();
}

function restoreFullCapture() {
  if (!trimmedForCookieless) return;
  trimmedForCookieless = false;
  posthog.set_config({ autocapture: true, capture_heatmaps: true });
  posthog.startSessionRecording();
}

function applyConsent() {
  if (!isPostHogConfigured()) return;
  let granted = false;
  try {
    const state = getTermly()?.getConsentState?.();
    // Grant PostHog if the visitor accepted analytics (or performance) cookies.
    granted = Boolean(state && (state.analytics || state.performance));
  } catch {
    granted = false;
  }

  if (granted) {
    restoreFullCapture();
    if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing();
      // The landing $pageview fired on mount while we were still opted out and
      // was DROPPED (opted-out events are discarded, not queued). Without this
      // re-capture, a first-time visitor who lands, accepts cookies and clicks
      // the CTA never registers step 1 of the acquisition funnel. Guarded by
      // the opted-out -> opted-in transition, so a returning consented visitor
      // (who starts opted in and whose mount pageview captured normally) does
      // NOT get a duplicate.
      posthog.capture("$pageview", { $current_url: window.location.href });
    }
  } else {
    trimToAnonymousCounts();
    if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing();
  }
}

function syncConsentFromTermly() {
  if (typeof window === "undefined") return;

  const subscribe = () => {
    const termly = getTermly();
    if (termly?.on) {
      // Fires once Termly is ready (picks up consent from a prior visit) and on
      // every change the visitor makes.
      termly.on("initialized", applyConsent);
      termly.on("consent", applyConsent);
      applyConsent();
      return true;
    }
    return false;
  };

  // Termly loads via afterInteractive, so window.Termly may not exist yet.
  // Try now; if it isn't ready, poll briefly until the CMP script has attached.
  if (subscribe()) return;
  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    if (subscribe() || tries >= 40) window.clearInterval(timer); // ~20s max
  }, 500);
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (!isPostHogConfigured()) return;
    if (typeof window === "undefined") return;
    if (posthog.__loaded) return;

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: POSTHOG_HOST,
      capture_pageview: false, // captured manually in PostHogPageView
      capture_pageleave: true,
      // --- the two options that make the cross-subdomain stitch work ---
      persistence: "cookie", // cookie (not localStorage) so it can be shared across subdomains
      cross_subdomain_cookie: true, // write distinct_id on `.launchlemonade.app`
      // --- behavioural capture to diagnose the landing -> signup leak ---
      // Autocapture (clicks/taps) + heatmaps show WHERE visitors click & scroll
      // on the landing pages; session replay lets us WATCH why individuals bounce.
      // All of this only records for visitors who accepted analytics cookies (the
      // consent gate below), so non-consenting traffic is never recorded.
      // Autocapture powers both the click/scroll heatmaps (heatmaps are enabled
      // at the PostHog project level — heatmaps_opt_in) and the autocaptured
      // click events. It's on by default; set explicitly to make intent clear.
      autocapture: true,
      session_recording: {
        // Mask text inputs by default; opt specific elements in/out via data-attrs.
        maskAllInputs: true,
        maskTextSelector: "[data-private]",
        // Record every consenting session — the paid plan removes the free-tier
        // replay cap that previously capped this at 0.25. Lower it again if
        // marketing traffic scales enough to strain the new plan's quota.
        sampleRate: 1.0,
      },
      // --- consent (GDPR) ---
      // Termly's resource-blocker auto-blocks *external <script> tags*, but PostHog
      // is bundled as an npm module, so the auto-blocker does not catch it. We must
      // gate it ourselves: start opted-OUT and opt in only once Termly reports that
      // the Analytics category has been granted (see syncConsentFromTermly).
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      // Opted out (declined, or no answer yet) = counted without cookies,
      // unless the visitor turned anonymous analytics off in the footer.
      ...(isAnonAnalyticsOff() ? {} : { cookieless_mode: "on_reject" as const }),
    });
    // Start trimmed: nobody has consented until Termly says so.
    trimToAnonymousCounts();

    syncConsentFromTermly();
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </>
  );
}

export { posthog };
